import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, X, CaretDown, Phone, MapPin } from "@phosphor-icons/react";
import { IMAGES, CONTACT } from "@/data/site";
import { MATRIZ, FILIAIS_RO, FILIAIS_MT } from "@/data/filiais";
import { WhatsAppButton } from "@/components/CTAButtons";
import { telUrl } from "@/lib/contact";

const TOP_LINKS = [
  { label: "Início", path: "/" },
  { label: "Serviços", path: "/servicos" },
  { label: "Quem Somos", path: "/quem-somos" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [filiaisOpen, setFiliaisOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setFiliaisOpen(false);
  }, [location.pathname]);

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const linkCls = (active) =>
    `font-heading text-sm font-medium uppercase tracking-wide transition-colors duration-200 ${
      active ? "text-brand-red" : "text-neutral-700 hover:text-black"
    }`;

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-black/10 bg-white/95 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 md:px-8">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img src={IMAGES.logo} alt="Auto Socorro Pimenta" className="h-9 w-auto md:h-11" />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {TOP_LINKS.map((item) => (
            <Link key={item.path} to={item.path} data-testid={`nav-${item.path}`} className={linkCls(isActive(item.path))}>
              {item.label}
            </Link>
          ))}

          <Link to={MATRIZ.path} data-testid="nav-matriz" className={linkCls(isActive(MATRIZ.path))}>
            Matriz
          </Link>

          <div
            className="relative"
            onMouseEnter={() => setFiliaisOpen(true)}
            onMouseLeave={() => setFiliaisOpen(false)}
          >
            <button data-testid="nav-filiais" onClick={() => setFiliaisOpen((o) => !o)} className={`flex items-center gap-1 ${linkCls(location.pathname.startsWith("/filial/") && location.pathname !== MATRIZ.path)}`}>
              Filiais <CaretDown className={`h-3 w-3 transition-transform duration-200 ${filiaisOpen ? "rotate-180" : ""}`} />
            </button>
            {filiaisOpen && (
              <div className="absolute left-1/2 top-full w-[420px] -translate-x-1/2 border border-black/10 bg-white p-6 shadow-xl">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-brand-red">Rondônia</p>
                    <ul className="space-y-1.5">
                      {FILIAIS_RO.map((f) => (
                        <li key={f.slug}>
                          <Link to={f.path} data-testid={`nav-${f.slug}`} className="block py-1 font-heading text-sm uppercase tracking-wide text-neutral-700 transition-colors hover:text-brand-red">
                            {f.city}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-brand-red">Mato Grosso</p>
                    <ul className="space-y-1.5">
                      {FILIAIS_MT.map((f) => (
                        <li key={f.slug}>
                          <Link to={f.path} data-testid={`nav-${f.slug}`} className="block py-1 font-heading text-sm uppercase tracking-wide text-neutral-700 transition-colors hover:text-brand-red">
                            {f.city}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          <Link to="/contato" data-testid="nav-/contato" className={linkCls(isActive("/contato"))}>
            Contato
          </Link>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a href={telUrl()} data-testid="header-phone" className="flex items-center gap-2 font-mono text-sm text-neutral-600 transition-colors hover:text-black">
            <Phone weight="fill" className="h-4 w-4 text-brand-red" />
            {CONTACT.phoneDisplay}
          </a>
          <WhatsAppButton label="WhatsApp" size="sm" testid="header-whatsapp" />
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="text-black lg:hidden"
          aria-label="Abrir menu"
        >
          {open ? <X className="h-7 w-7" /> : <List className="h-7 w-7" />}
        </button>
      </div>

      {open && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-black/10 bg-white lg:hidden" data-testid="mobile-menu">
          <nav className="flex flex-col px-5 py-4">
            {TOP_LINKS.map((item) => (
              <Link key={item.path} to={item.path} className="border-b border-black/10 py-3 font-heading uppercase tracking-wide text-neutral-800">
                {item.label}
              </Link>
            ))}
            <Link to={MATRIZ.path} className="flex items-center gap-2 border-b border-black/10 py-3 font-heading uppercase tracking-wide text-neutral-800">
              <MapPin weight="fill" className="h-4 w-4 text-brand-red" /> Matriz — {MATRIZ.city}
            </Link>

            <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-brand-red">Filiais · Rondônia</p>
            {FILIAIS_RO.map((f) => (
              <Link key={f.slug} to={f.path} className="border-b border-black/10 py-2.5 font-heading text-sm uppercase tracking-wide text-neutral-700">
                {f.city}
              </Link>
            ))}
            <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-brand-red">Filiais · Mato Grosso</p>
            {FILIAIS_MT.map((f) => (
              <Link key={f.slug} to={f.path} className="border-b border-black/10 py-2.5 font-heading text-sm uppercase tracking-wide text-neutral-700">
                {f.city}
              </Link>
            ))}

            <Link to="/contato" className="border-b border-black/10 py-3 font-heading uppercase tracking-wide text-neutral-800">Contato</Link>
            <div className="pt-4">
              <WhatsAppButton className="w-full" testid="mobile-whatsapp" />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
